import { Offcanvas, Form, Row, Col, Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import ReactQuill from "react-quill-new";
import { quillImageHandler } from "../../utility/quilImageHandler";
import CommonQuill from "../../components/editor/CommonQuill";
import HelperService from "../../utility/HelperService";
import { XCircleFill } from "react-bootstrap-icons";

interface EventFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

/**
 * Tag interface for tag objects returned by the API.
 * Add fields here as needed if the API returns additional properties.
 */
interface Tag {
  _id: string;
  name: string;
}

type FormData = {
  title: string;
  description: string;
  tagId: string;
  resource_type: string;
  publishedAt: string;
  keywords?: string;
  meta_description?: string;
  slug?: string;
  createdBy?: string;
  amount?: number | string;
  status: "PUBLISHED" | "UNPUBLISHED";
};

const AddIndustryReport = ({ show, handleClose, data }: EventFormData) => {
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>();

  const [isLoading, setLoading] = useState(false);
  // const [banner, setBanner] = useState<File | string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | string | null>(null);
  const [value, setEditorValue] = useState("");
  const [tagsList, setTagsList] = useState<any[]>([]);
  const [pdfError, setPdfError] = useState<string>("");
  const quillRef = useRef<any>(null);
  const [slugEdited, setSlugEdited] = useState(false);
  const [customErrors, setCustomErrors] = useState<{
    description?: string;
  }>({});

  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const handleRemovePdf = () => {
    setPdfFile(null);
    setPdfError("");

    if (pdfInputRef.current) {
      pdfInputRef.current.value = "";
    }
  };

  const titleValue = watch("title");
useEffect(() => {
  if (data?._id) return; // don't auto-generate slug when editing
  if (!titleValue) {
    setValue("slug", "");
    return;
  }

  const generatedSlug = HelperService.generateSlug(titleValue);

  setValue("slug", generatedSlug, {
    shouldValidate: true,
    shouldDirty: true,
  });

}, [titleValue]);

  useEffect(() => {
    if (show) {
      fetchTags();
      const storedUser = localStorage.getItem("UserData");
      const userData = storedUser ? JSON.parse(storedUser) : null;
      const formattedDate = data?.publishedAt
        ? new Date(data.publishedAt).toISOString().split("T")[0]
        : "";
      reset({
        title: data?.title || "",
        amount: data?.amount || "",
        description: data?.description || "",
        resource_type: "HOME",
        tagId: data?.tagId?._id ?? "",
        publishedAt: formattedDate,
        keywords: data?.keywords || "",
        meta_description: data?.meta_description || "",
        slug: data?.slug || "",
        createdBy: userData?.fullName || "",
        status: data?.status || "PUBLISHED",
      });
      setEditorValue(data?.description || "");

      // ✅ preload existing banner & file
      // if (data?.banner) {
      //   setBanner(data.banner);
      // }
      if (data?.file) {
        setPdfFile(data.file);
      }
    } else {
      reset({});
      // setBanner(null);
      setPdfFile(null);
    }
  }, [show, data, reset]);
  // ✅ Fetch paginated tags
  const fetchTags = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: `tags/get-tags/HOME`, // no pagination params
        body: null,
      });

      const tags: Tag[] = res?.data || [];
      setTagsList(tags);

      // ✅ If editing existing data, auto-select the tag
      // if (data?.tagId) {
      //   const tagExists = tags.some((tag: Tag) => tag._id === data.tagId);
      //   if (tagExists) setValue("tagId", data.tagId);
      // }
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };
  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    // remove html tags, &nbsp; and spaces
    const text = value   
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };
const validateInfoTab = async () => {
  const titleValid = await trigger(["title"]);

  const newErrors: any = {};

  if (isQuillEmpty(value)) {
    newErrors.description = "Description is required";
  }

  setCustomErrors(newErrors);

  // 🔥 IMPORTANT: return actual validity
  return titleValid && !newErrors.description;
};
  const onSubmit = async (request: FormData) => {
    if (!pdfFile) {
      setPdfError("PDF file is required");
      toast.error("Please upload a PDF file");
      return;
    }
const valid = await validateInfoTab();
if (!valid) {
  toast.error("Please fill required fields");
  return;
}

    if (data?._id && typeof pdfFile === "string") {
      setPdfError("");
    }

    setPdfError("");
    setLoading(true);
    const formData = new FormData();
    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    formData.append("title", request.title);

    const editor = quillRef.current?.getEditor();
    const html = editor?.root.innerHTML || "";
    formData.append("description", html || "");
    formData.append("resource_type", "HOME");
    formData.append("tagId", request.tagId || "");
    formData.append("keywords", request.keywords || "");
    formData.append("meta_description", request.meta_description || "");
    formData.append("amount", request.amount?.toString() || "0");
    formData.append("slug", request.slug || "");
    formData.append("status", request.status);
    if (request.publishedAt)
      formData.append("publishedAt", request.publishedAt);
    // if (banner && banner instanceof File) {
    //   formData.append("banner", banner); // only send new banner file
    // }
    if (pdfFile && pdfFile instanceof File) {
      formData.append("file", pdfFile); // only send new pdf file
    }
    if (userData?.id) {
      formData.append("createdBy", userData.id);
    }
    const apiCall = data?._id
      ? WebService.fileUploadPutAPI({
          action: `resources/edit-resource/${data._id}`,
          body: formData,
        })
      : WebService.fileUploadAPI({
          action: `resources/add-resource`,
          body: formData,
        });

    apiCall
      .then((res: any) => {
        toast.success(res.message);
        reset();
        handleClose();
        // setBanner(null);
        setPdfFile(null);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error.response?.data?.message || "An error occurred");
      });
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        // setBanner(null);
        setPdfFile(null);
        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {data ? "Edit Home Page Report" : "Add Home Page Report"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Main Content
            </span>
            {/* Title */}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Title <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title"
                {...register("title", { required: "Title is required" })}
              />
              {errors.title && (
                <div className="error-message">{errors.title.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="slugField">
              <Form.Label className="label">Slug</Form.Label>{" "}
              <span className="text-danger">*</span>
 <Form.Control
                type="text"
                placeholder="Auto-generated slug"
                {...register("slug", { required: "Slug is required" })}
                disabled={!data?._id}
                readOnly={!data?._id}
              />
              {errors.slug && (
                <div className="error-message">{errors.slug.message}</div>
              )}
            </Form.Group>
            {/* Description */}
            <Form.Group className="mb-2" controlId="eventDescription">
              <Form.Label className="label">
                Description <span className="text-danger">*</span>
              </Form.Label>
              {/* <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value}
                onChange={setEditorValue}
                className="editor"
                modules={{
                  toolbar: {
                    container: [
                      // [
                      //   {
                      //     size: [
                      //       "8px",
                      //       "10px",
                      //       "12px",
                      //       "14px",
                      //       "16px",
                      //       "18px",
                      //       "24px",
                      //       "32px",
                      //       "48px",
                      //     ],
                      //   },
                      // ],

                      ["bold", "italic", "underline"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ indent: "-1" }, { indent: "+1" }],
                      [{ size: ["small", false, "large"] }],
                      [{ color: [] }, { background: [] }],
                      ["link", "image", "emoji"],
                      ["clean"],
                    ],
                    handlers: {
                      image: () =>
                        quillImageHandler({
                          quillRef,
                          storage: "resources",
                        }),
                    },
                  },

                  "emoji-toolbar": true,
                  "emoji-textarea": true,
                  "emoji-shortname": true,
                }}
              /> */}
              <CommonQuill
                ref={quillRef}
                value={value}
                onChange={setEditorValue}
                storage="resources"
                initialHtml={show && data?.description ? HelperService.normalizeResizeHtml(data.description) : undefined}
                onClearError={() =>
                  setCustomErrors((p) => ({
                    ...p,
                    description: undefined,
                  }))
                }
              />
              {customErrors.description && (
                <div className="error-message">{customErrors.description}</div>
              )}

              {/* {!value && (
                <div className="error-message">Description is required</div>
              )} */}
            </Form.Group>

            {/* ➕ Amount */}
            {/* <Form.Group className="mb-2" controlId="amountField">
              <Form.Label className="label">
                Amount <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter amount"
                {...register("amount", { required: "Amount is required" })}
              />
              {errors.amount && (
                <div className="error-message">{errors.amount.message}</div>
              )}
            </Form.Group> */}

            {/* <Form.Group className="mb-2" controlId="eventDate">
              <Form.Label className="label">
                Published Date <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                min={new Date().toISOString().split("T")[0]}
                type="date"
                {...register("publishedAt", { required: "Date is required" })}
              />
              {errors.publishedAt && (
                <div className="error-message">
                  {errors.publishedAt.message}
                </div>
              )}
            </Form.Group> */}
            {/* Tags Dropdown */}
            {/* <Form.Group className="mb-2" controlId="eventTag">
              <Form.Label className="label">
                Tag <span className="text-danger">*</span>
              </Form.Label>

              <Form.Select
                {...register("tagId", { required: "Please select a tag" })}
                size="sm"
              >
                <option value="">Select Tag</option>
                {tagsList.map((tag: { _id: string; name: string }) => (
                  <option key={tag._id} value={tag._id}>
                    {tag.name}
                  </option>
                ))}
              </Form.Select>

              {errors.tagId && (
                <div className="error-message">{errors.tagId.message}</div>
              )}
            </Form.Group> */}

            <Form.Group className="mb-2" controlId="createdBy">
              <Form.Label className="label">Created By</Form.Label>
              <Form.Control
                type="text"
                placeholder="Created by user"
                {...register("createdBy")}
                value={watch("createdBy") || ""}
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="status">
              <Form.Label className="label">
                Status <span className="text-danger">*</span>
              </Form.Label>

              <Form.Select
                {...register("status", { required: "Status is required" })}
                size="sm"
              >
                <option value="PUBLISHED">Published</option>
                <option value="UNPUBLISHED">Unpublished</option>
              </Form.Select>

              {errors.status && (
                <div className="error-message">{errors.status.message}</div>
              )}
            </Form.Group>

            {/* <Form.Group className="mb-2" controlId="isFeatured">
              <Form.Check
                type="checkbox"
                label="Featured"
                {...register("featured")}
              />
            </Form.Group> */}
          </Card>

          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Media
            </span>
            <Row>
              {/* Removed banner upload as per requirement 6 Nov 2025*/}
              {/* Banner Upload */}
              {/* <Col>
                <Form.Group className="mb-2" controlId="bannerFile">
                  <Form.Label className="label">Upload Banner</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    className="custom-file-upload"
                    onChange={(e) => {
                      const input = e.target as HTMLInputElement;
                      const selectedFile = input.files && input.files[0];
                      if (selectedFile) {
                        setBanner(selectedFile);
                      }
                    }}
                  />
                </Form.Group>

                {banner && (
                  <div className="mb-2 event-file">
                    <img
                      src={
                        banner instanceof File
                          ? URL.createObjectURL(banner)
                          : banner
                      }
                      alt="Banner Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}
              </Col> */}
              <Col>
                {/* PDF Upload */}
                <Form.Group className="mb-2" controlId="pdfFile">
                  <Form.Label className="label">
                    Upload PDF File <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="file"
                    ref={pdfInputRef}
                    accept=".pdf"
                    className="custom-file-upload"
                    onChange={(e) => {
                      const input = e.target as HTMLInputElement;
                      const selectedFile = input.files && input.files[0];

                      if (!selectedFile) {
                        setPdfFile(null);
                        setPdfError("PDF file is required");
                        return;
                      }

                      if (selectedFile) {
                        setPdfFile(selectedFile);
                        setPdfError("");
                      }
                    }}
                  />
                  <span className="text-primary" style={{ fontSize: "12px" }}>
                    Note: Only PDF files are allowed. Maximum upload size is 100 MB
                    MB{" "}
                  </span>
                  {pdfError && <div className="error-message">{pdfError}</div>}
                </Form.Group>
                {/* PDF Preview */}
                {pdfFile && (
                  <div className="mb-2 d-flex align-items-center gap-2">

                    <a
                      href={
                        pdfFile instanceof File
                          ? URL.createObjectURL(pdfFile)
                          : (pdfFile as string)
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View PDF
                    </a>

                    <XCircleFill
                      size={18}
                      className="text-danger"
                      role="button"
                      style={{ cursor: "pointer" }}
                      onClick={handleRemovePdf}
                      title="Remove PDF"
                    />

                  </div>
                )}
              </Col>
            </Row>
          </Card>

          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              SEO
            </span>
            <Form.Label className="label">
              Meta Description <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Meta Description"
              {...register("meta_description", {
                required: "Meta Description is required",
              })}
            />
            {errors.meta_description && (
              <div className="error-message">
                {errors.meta_description.message}
              </div>
            )}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Keywords <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter keywords"
                {...register("keywords", { required: "Keywords are required" })}
              />
              {errors.keywords && (
                <div className="error-message">{errors.keywords.message}</div>
              )}
            </Form.Group>
          </Card>
        </Form>
      </Offcanvas.Body>
      <div className="d-flex justify-content-end p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddIndustryReport;
